import { useEffect, useMemo, useRef, useState } from 'react';
import Datatable from '@/components/common/Datatable';
import { Box, Divider, Icon, Text, useDisclosure } from '@chakra-ui/react';
import { Column } from 'react-table';
import {
  useCreateCouponMutation,
  useDeleteCouponMutation,
  useGetCouponList,
} from '@/services/coupon';
import { ICoupon } from '@/services/coupon/types';
import { convertDateFormat } from '@/utils/convertDateFormat';
import Button from '@/components/common/Button';
import FormModal from '@/components/common/FormModal';
import CreateCouponForm, { ICreateCouponData } from './CreateCouponForm';
import useCustomToast from '@/components/common/Toast';
import useErrorHandler from '@/utils/errorHandler';
import { AxiosError } from 'axios';
import deleteIcon from '../../assets/svg/delete-bg.svg?react';
import CustomConfirmationModal from '@/components/common/ConfirmationModal';
import ConfirmDeleteIcon from '@/assets/svg/delete-bg.svg?react';
import { getCurrencySymbol } from '@/utils/currencyUtils';
import { useStore } from '@/store/store';

const VoucherManagement = () => {
  const { user } = useStore(); // Access user from global store
  const currency = user?.currency || 'USD'; // Default to 'USD' if currency is undefined

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<{
    start_date?: string;
    end_date?: string;
  }>({
    start_date: '',
    end_date: '',
  });
  const { showToast } = useCustomToast();
  const { handleError } = useErrorHandler();
  const [selectedCouponId, setSelectedCouponId] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    isOpen: isCreateCouponOpen,
    onOpen: onCreateCouponOpen,
    onClose: onCreateCouponClose,
  } = useDisclosure();

  const {
    data: Products,
    refetch,
    isLoading,
    isError,
  } = useGetCouponList({
    page: pagination.page,
    query,
    ...filters,
  });

  const {
    mutateAsync: createCoupon,
    isLoading: isCouponCreateLoading,
    isSuccess,
  } = useCreateCouponMutation();

  const { mutateAsync: deleteCoupon, isLoading: isCouponDeleteLoading } = useDeleteCouponMutation();

  useEffect(() => {
    if (Products) {
      setPagination((prev) => ({
        ...prev,
        pageSize: 10,
        totalCount: Products.count,
        totalPages: Products.page_count,
      }));
    }
  }, [Products]);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleSearch = (searchTerm: string) => {
    setQuery(searchTerm);
    setPagination((prev) => ({ ...prev, page: 1 }));
    refetch();
  };

  const applyFilters = () => {
    refetch();
  };

  const resetFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  useEffect(() => {
    if (filters.start_date === '' && filters.end_date === '') {
      refetch();
    }
  }, [filters, pagination.page]);

  const submitRef = useRef<() => void>(null);

  const handleCreateCouponSubmit = async (data: ICreateCouponData) => {
    try {
      await createCoupon(data);
      onCreateCouponClose();
      showToast('success', 'Coupon Created Successfully.');
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        handleError(err);
      }
    }
  };

  const handleDeleteConfirm = () => {
    if (selectedCouponId !== null) {
      try {
        deleteCoupon(selectedCouponId).then(() => {
          setSelectedCouponId(null);
          onClose();
          showToast('success', 'Coupon Deleted Successfully');
        });
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          handleError(error);
        }
      }
    }
  };

  const columns: Column<ICoupon>[] = useMemo(
    () => [
      {
        Header: 'Product ID',
        accessor: 'id',
      },
      {
        Header: 'Promotion Name',
        Cell: ({ row }: { row: { original: ICoupon } }) => {
          const total = row.original.code;
          return total ? (
            <Text minWidth={'100px'}>{total}</Text>
          ) : (
            <Divider orientation="horizontal" width={'20px'} borderColor={'text.primary'} />
          );
        },
      },
      {
        Header: 'Promotion Type',
        Cell: ({ row }: { row: { original: ICoupon } }) => {
          const type = row.original.coupon_type;
          return type ? (
            <Text minWidth={'150px'}>{type}</Text>
          ) : (
            <Divider orientation="horizontal" width={'20px'} borderColor={'text.primary'} />
          );
        },
      },
      {
        Header: 'Voucher Redeem Period',
        Cell: ({ row }: { row: { original: ICoupon } }) => {
          const period = `${convertDateFormat(
            row.original.start_at,
          )} to ${convertDateFormat(row.original.end_at)}`;
          return period ? (
            <Text minWidth={'150px'}>{period}</Text>
          ) : (
            <Divider orientation="horizontal" width={'20px'} borderColor={'text.primary'} />
          );
        },
      },
      {
        Header: 'Min. Spend',
        Cell: ({ row }: { row: { original: ICoupon } }) => {
          const total = row.original.min_purchase;
          return total ? (
            <Text minWidth={'100px'}>
              {getCurrencySymbol(currency)}
              {total}
            </Text>
          ) : (
            <Divider orientation="horizontal" width={'20px'} borderColor={'text.primary'} />
          );
        },
      },
      {
        Header: 'Discount',
        Cell: ({ row }: { row: { original: ICoupon } }) => {
          const discountType =
            row.original.coupon_type === 'Percentage Discount' ? 'percentage' : 'money_value';
          const total =
            discountType === 'percentage'
              ? `${row.original.discount_percent}%`
              : `${getCurrencySymbol(currency)}${row.original.money_value_off}`;
          return total ? (
            <Text minWidth={'100px'}>{total}</Text>
          ) : (
            <Divider orientation="horizontal" width={'20px'} borderColor={'text.primary'} />
          );
        },
      },
      {
        Header: 'Limit Per Customer',
        Cell: ({ row }: { row: { original: ICoupon } }) => {
          const limit = row.original.coupon_limit_per_customer;
          return limit ? (
            <Text minWidth={'150px'}>{limit}</Text>
          ) : (
            <Divider orientation="horizontal" width={'20px'} borderColor={'text.primary'} />
          );
        },
      },
      {
        Header: 'Total Voucher Issued',
        Cell: ({ row }: { row: { original: ICoupon } }) => {
          const total_issue = row.original.total_issues;
          return total_issue ? (
            <Text minWidth={'150px'}>{total_issue}</Text>
          ) : (
            <Divider orientation="horizontal" width={'20px'} borderColor={'text.primary'} />
          );
        },
      },
      {
        Header: 'Actions',
        Cell: ({ row }: { row: { original: ICoupon } }) => {
          return (
            <Icon
              as={deleteIcon}
              boxSize={5}
              color={'status.error'}
              cursor={'pointer'}
              onClick={() => {
                setSelectedCouponId(row.original.id?.toString());
                onOpen();
              }}
            />
          );
        },
      },
    ],
    [Products, currency],
  );

  return (
    <Box p={6} fontFamily={'primary'}>
      <Datatable
        data={Products?.results ?? []}
        isLoading={isLoading}
        isError={isError}
        columns={columns}
        headerButton={<Button onClick={onCreateCouponOpen}>+ Create Coupon</Button>}
        pagination={{
          page: pagination.page,
          pageSize: pagination.pageSize,
          totalCount: pagination.totalCount,
          totalPages: pagination.totalPages,
        }}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        filters={filters}
        setFilters={setFilters}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
        hideCategoryFilter={true}
        sticky={true}
      />
      <FormModal
        title="Create Coupon"
        isOpen={isCreateCouponOpen}
        size="2xl"
        onConfirm={() => submitRef.current && submitRef.current()}
        isLoading={isCouponCreateLoading}
        children={
          <CreateCouponForm
            ref={submitRef}
            onSubmit={handleCreateCouponSubmit}
            isSuccess={isSuccess}
            currency={currency}
          />
        }
        onClose={onCreateCouponClose}
      />
      <CustomConfirmationModal
        isLoading={isCouponDeleteLoading}
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleDeleteConfirm}
        icon={ConfirmDeleteIcon}
        title="Delete Coupon?"
        subtitle="Are you sure you want to delete this coupon?"
        description="This action cannot be undone, and you won't be able to track this coupon anywhere. This will remove the coupon permanently."
        buttonText="Confirm"
      />
    </Box>
  );
};

export default VoucherManagement;
